import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'

export interface Props {
  description?: string
  subtitle?: string
  title?: string
}

export const DigitalConcierge = memo(function DigitalConcierge({
  description,
  subtitle,
  title,
}: Props) {
  if (!description || !subtitle || !title) {
    return null
  }

  return (
    <Container>
      <Icon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20.25"
          height="36"
          viewBox="0 0 20.25 36"
        >
          <path
            d="M20338.775,10624a2.78,2.78,0,0,1-2.775-2.774v-30.448a2.783,2.783,0,0,1,2.775-2.777h14.7a2.778,2.778,0,0,1,2.773,2.777v30.448a2.774,2.774,0,0,1-2.773,2.774Zm-1.65-33.223v30.448a1.654,1.654,0,0,0,1.65,1.649h14.7a1.65,1.65,0,0,0,1.648-1.649v-30.448a1.654,1.654,0,0,0-1.648-1.652h-1.727v.844a1.666,1.666,0,0,1-1.687,1.688h-7.875a1.656,1.656,0,0,1-1.2-.489,1.676,1.676,0,0,1-.49-1.2v-.844h-1.725A1.658,1.658,0,0,0,20337.125,10590.779Zm12.938-.246a.542.542,0,0,0,.563-.562v-.844h-9v.844a.558.558,0,0,0,.16.4.547.547,0,0,0,.4.158Zm-4.332,25.313-6.187-6.186a.564.564,0,0,1,.227-.99.569.569,0,0,1,.566.2l5.789,5.791,5.793-5.791v0a.565.565,0,0,1,.559-.184.566.566,0,0,1,.234.977l-6.189,6.186a.559.559,0,0,1-.791,0Zm.293-4.719-.07-.021h-.035l-.074-.041-.027,0-.086-.07-6.187-6.187a.563.563,0,0,1,.793-.8l5.227,5.232v-11.561a.563.563,0,0,1,1.125,0v11.561l5.23-5.232-.008,0a.563.563,0,0,1,.795.8l-6.186,6.186-.09.07h-.023l-.055.041h-.033l-.07.021a.556.556,0,0,1-.113.011A.538.538,0,0,1,20346.023,10611.127Z"
            transform="translate(-20336 -10588.002)"
          />
        </svg>
      </Icon>
      {title ? <Title>{title}</Title> : null}
      {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
      {description ? (
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      ) : null}
      <Store dial={5} row wrap>
        <Button
          href="https://apps.apple.com/it/app/keesy/id1491076028"
          aria-label="App Store"
          rel="noreferrer"
          target="_blank"
          style={{ backgroundImage: `url(/app-store-button.svg)` }}
        />
        <Button
          href="https://play.google.com/store/apps/details?id=com.keesy.guest.app"
          aria-label="Google Play"
          rel="noreferrer"
          target="_blank"
          style={{
            backgroundImage: `url(/google-play-button.svg)`,
          }}
        />
      </Store>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.neutralDark1};
  padding: 7.5rem 10vw 3.75rem;
  text-align: center;

  @media (max-width: 1199px) {
    background: none;
    margin-bottom: 5.25rem;
    padding: 0;
  }
`

const Icon = styled.div`
  margin-bottom: 1.25rem;
  svg {
    width: auto;
    height: 2.25rem;
    fill: ${({ theme }) => theme.colors.variants.primaryLight1};
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryLight1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2rem;
  line-height: 2.375rem;
  margin-bottom: 0.375rem;

  @media (max-width: 1199px) {
    color: ${({ theme }) => theme.colors.variants.neutralDark1};
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.1313rem;
    line-height: 1.25rem;
    text-transform: uppercase;
  }
`

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.875rem;

  @media (max-width: 1199px) {
    color: ${({ theme }) => theme.colors.variants.neutralDark1};
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  line-height: 1.75rem;
  margin-top: 1.875rem;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Store = styled(FlexBox)`
  margin-top: 1.875rem;

  @media (max-width: 1199px) {
    display: block;
    margin-top: 0;
  }
`

const Button = styled.a`
  width: 10.125rem;
  height: 3rem;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  margin: 0 0.375rem;

  @media (max-width: 1199px) {
    display: block;
    margin: 1.25rem auto 0;
  }
`
